import axios, { AxiosError } from 'axios';

// API configuration
const API_BASE_URL = 'https://api.bfore.ai/v2/cti';

// Get token from localStorage (same pattern as apiClient.ts)
const getToken = (): string | null => {
  return window.localStorage.getItem('userToken');
};

// TypeScript interfaces
interface DomainCharacter {
  domain: string;
  ip?: string;
  score: number;
  character: 'requested' | 'bad' | 'good' | 'nrd' | 'bgt-group' | 'ggt-group';
  color: string;
  isGroupNode?: boolean;
  groupedDomains?: string[];
}

interface DomainConnection {
  from: string;
  to: string;
}

interface GraphData {
  status?: boolean;
  message?: string;
  domainCharacters: DomainCharacter[];
  domainConnections: DomainConnection[];
}

export interface DomainData {
  domainId: string;
  processId: string;
  processName: string;
  domainScore: number;
  scoreDate: string;
  category: string;
  processCount: number;
  method: string;
  dgaScore: number;
  graphData: GraphData;
}

interface ApiResponse {
  status: boolean;
  data?: DomainData;
  message?: string;
}

interface ErrorResponse {
  message?: string;
}

/**
 * Fetch domain threat intelligence data with a provided token
 * This is the recommended method when using Auth0
 * @param domain - The domain name to analyze
 * @param token - The Auth0 access token
 * @returns Promise with domain data including graph information
 */
export const fetchDomainDataWithToken = async (
  domain: string,
  token: string
): Promise<ApiResponse> => {
  try {
    const response = await axios.get<ApiResponse>(
      `${API_BASE_URL}/request/${encodeURIComponent(domain)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;

    if (axiosError.response) {
      // Server responded with an error status
      const errorMessage = axiosError.response.data?.message || 'Failed to fetch domain data';
      throw new Error(errorMessage);
    } else if (axiosError.request) {
      // Request was made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(axiosError.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Fetch domain threat intelligence data using token from localStorage
 * This follows the same pattern as apiClient.ts
 * @param domain - The domain name to analyze
 * @returns Promise with domain data including graph information
 */
export const fetchDomainData = async (domain: string): Promise<ApiResponse> => {
  const token = getToken();

  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  return fetchDomainDataWithToken(domain, token);
};

export default {
  fetchDomainData,
  fetchDomainDataWithToken
};
